// Local wire types for the menu surface — hand-authored against server/src/
// models/menu.rs (the shapes the testbed's catalog_full story proves). No
// code is shared with the server: the contract is the document.

export type Audit = { createdAt: string; updatedAt: string; modifiedAt: number; modifiedBy: string };

export type Category = {
  categoryId: string;
  name: string;
  description: string | null;
  imageUrl: string | null;
  backgroundColor: string | null;
  sortOrder: string;
} & Audit;

export type CategoryRequest = {
  name: string;
  description: string | null;
  imageUrl: string | null;
  backgroundColor: string | null;
};

/** A flat price with its per-channel extras (cents). */
export type Price = {
  priceInCents: number;
  pickupExtraInCents: number;
  deliveryExtraInCents: number;
  dineInExtraInCents: number;
  uberEatsExtraInCents: number;
};

export type ModifierAttachment = {
  modifierId: string;
  minSelectOverride: number | null;
  maxSelectOverride: number | null;
};

/** A pizza's price at one size — the flat price shape plus the size and the
 * half-and-half switch. */
export type PizzaSizePrice = Price & { sizeId: string; halfAndHalfAvailable: boolean };

export type SlotOption = { itemId: string; extraChargeInCents: number; isDefault: boolean };

export type SlotType = 'generic' | 'pizza';

/** One pick in a bundle: a generic slot offers generic items; a pizza slot
 * pins one size and offers pizzas (half & half when allowed). */
export type BundleSlot = {
  slotId: string | null;
  slotType: SlotType;
  name: string;
  minSelect: number;
  maxSelect: number;
  allowRepeat: boolean;
  allowHalfAndHalf: boolean | null;
  sizeId: string | null;
  options: SlotOption[];
};

export type ItemType = 'generic' | 'pizza' | 'half_and_half' | 'bundle';

/** The fields every item has (menu_item_table). */
export type ItemCommon = {
  categoryId: string | null;
  name: string;
  description: string | null;
  soldOut: boolean;
  available: boolean;
  availablePickup: boolean;
  availableDelivery: boolean;
  availableDineIn: boolean;
  isGstApplicable: boolean;
  isAlcohol: boolean;
  imageUrl: string | null;
};

/** The type-specific half, tagged by `itemType` and flattened into the item. */
export type Definition =
  | { itemType: 'generic'; price: Price }
  | { itemType: 'pizza'; sizes: PizzaSizePrice[]; defaultToppings: string[] }
  | { itemType: 'half_and_half'; sizeId: string }
  | { itemType: 'bundle'; price: Price | null; slots: BundleSlot[] };

export type Item = ItemCommon &
  Definition & {
    itemId: string;
    itemClass: 'single' | 'bundle';
    sortOrder: string;
    modifiers: ModifierAttachment[];
  } & Audit;

/** The typed create/replace payload — one document, one POST/PUT. */
export type ItemRequest = ItemCommon & Definition & { modifiers: ModifierAttachment[] };

export type ModifierOption = { optionId: string; name: string; extraChargeInCents: number; isDefault: boolean };

export type Modifier = {
  modifierId: string;
  name: string;
  minSelect: number;
  maxSelect: number;
  sortOrder: string;
  options: ModifierOption[];
} & Audit;

export type ModifierRequest = {
  name: string;
  minSelect: number;
  maxSelect: number;
  /** a known optionId keeps the option's identity across a replace */
  options: { optionId?: string | null; name: string; extraChargeInCents: number; isDefault: boolean }[];
};

export type SizeBase = { baseId: string; extraChargeInCents: number };

export type PizzaSize = {
  sizeId: string;
  name: string;
  halfAndHalfEnabled: boolean;
  halfAndHalfExtraChargeInCents: number;
  sortOrder: string;
  bases: SizeBase[];
} & Audit;

export type PizzaSizeRequest = {
  name: string;
  halfAndHalfEnabled: boolean;
  halfAndHalfExtraChargeInCents: number;
  bases: SizeBase[];
};

export type PizzaBase = { baseId: string; name: string; docketLabel: string | null; sortOrder: string } & Audit;
export type PizzaBaseRequest = { name: string; docketLabel: string | null };

export type ToppingPrice = { sizeId: string; priceInCents: number };

export type PizzaTopping = { toppingId: string; name: string; soldOut: boolean; sortOrder: string; prices: ToppingPrice[] } & Audit;
export type PizzaToppingRequest = { name: string; soldOut: boolean; prices: ToppingPrice[] };

export const ITEM_TYPES: ItemType[] = ['generic', 'pizza', 'half_and_half', 'bundle'];

export const TYPE_LABEL: Record<string, string> = {
  generic: 'Generic',
  pizza: 'Pizza',
  half_and_half: 'Half & half',
  bundle: 'Bundle',
};

export const TYPE_BLURB: Record<string, string> = {
  generic: 'Flat-priced item with optional modifier groups. Can fill a bundle slot.',
  pizza: 'Priced per size from the store pizza sizes; carries default toppings.',
  half_and_half: 'Two halves of any pizza sold at one size — priced by rule.',
  bundle: 'A deal: slots that offer generic items or pizzas, at a fixed or summed price.',
};

export const emptyPrice = (): Price => ({
  priceInCents: 0,
  pickupExtraInCents: 0,
  deliveryExtraInCents: 0,
  dineInExtraInCents: 0,
  uberEatsExtraInCents: 0,
});

/** The headline price of an item for a list row. */
export function priceOf(it: Item): Price | null {
  if (it.itemType === 'generic') return it.price;
  if (it.itemType === 'bundle') return it.price;
  return null;
}

export const errMsg = (e: unknown, fallback: string): string => (e instanceof Error ? e.message : fallback);

export const byRank = <T extends { sortOrder: string }>(a: T, b: T): number =>
  a.sortOrder < b.sortOrder ? -1 : a.sortOrder > b.sortOrder ? 1 : 0;
